import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { saveAs } from 'file-saver';
import api from '../../services/httpService';

export const base = '/api/airport-badges';
export const inputClass = 'ab-input mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base focus:border-red-500 focus:ring-red-500';
export const buttonClass = 'ab-button min-h-[44px] rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50';
export const primaryClass = 'ab-primary min-h-[44px] rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50';
export const cardClass = 'ab-card min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm';

export function errorText(error) {
  const fallback = 'The request could not be completed. Please retry.';
  const data = error?.response?.data;
  if (error?.response?.status === 503 && data?.code === 'airport_badges_protection_unavailable') {
    return 'Protected employee details are unavailable because secure storage needs administrator attention. Contact your administrator to check the Airport Badges encryption configuration, then try again.';
  }
  // Server/proxy errors can contain entire debug pages, SQL or stack traces.
  // Only structured client-error messages belong in the badge interface.
  if (error?.response?.status >= 500 || !data || typeof data !== 'object' || data instanceof Blob) return fallback;
  const flatten = value => typeof value === 'string' ? value : Array.isArray(value) ? value.map(flatten).join(' ') : value && typeof value === 'object' ? Object.entries(value).map(([key, item]) => `${key === 'detail' ? '' : key.replaceAll('_', ' ') + ': '}${flatten(item)}`).join(' ') : String(value);
  const message = flatten(data);
  return !message || message.length > 600 || /<[^>]+>|Traceback \(most recent call last\)/i.test(message) ? fallback : message;
}

export function useBadgeData(path, revision = 0) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!path);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!path) { setData(null); setLoading(false); return; }
    let active = true;
    setLoading(true); setError('');
    api.get(base + path).then(response => { if (active) setData(response.data); })
      .catch(err => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [path, revision, reload]);
  return { data, error, loading, refresh: () => setReload(value => value + 1) };
}

export function useBadgeCollection(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    (async () => {
      try {
        if (!path) { if (active) setData(null); return; }
        let page = 1, next = true;
        const results = [];
        while (active && next) {
          const response = await api.get(`${base}${path}${path.includes('?') ? '&' : '?'}page=${page}`);
          results.push(...response.data.results); next = !!response.data.next; page += 1;
        }
        if (active) setData({ results });
      } catch (err) { if (active) setError(errorText(err)); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [path, revision]);
  return { data, error, loading, refresh: () => setRevision(value => value + 1) };
}

export function LoadState({ state, children }) {
  if (state.loading) return <p className="py-10 text-center text-slate-500" role="status">Loading…</p>;
  if (state.error) return <div role="alert" className="rounded-lg bg-red-50 p-4 text-red-800">{state.error}<button className="ml-3 min-h-[44px] underline" onClick={state.refresh}>Retry</button></div>;
  return children;
}

export function Field({ label, type = 'text', value, onChange, required = false, children, ...props }) {
  return <label className="block min-w-0 text-sm font-medium text-slate-700">{label}{required && ' *'}
    {children ? <select className={inputClass} value={value ?? ''} onChange={event => onChange(event.target.value)} required={required} {...props}>{children}</select>
      : type === 'textarea' ? <textarea className={inputClass} rows={3} value={value ?? ''} onChange={event => onChange(event.target.value)} required={required} {...props} />
        : <input className={inputClass} type={type} value={value ?? ''} onChange={event => onChange(event.target.value)} required={required} {...props} />}
  </label>;
}

export function Modal({ title, onClose, children, onSubmit, busy, error, submitLabel = 'Save', large = false, kicker, readOnly = false }) {
  return <Dialog open onClose={() => !busy && onClose()} className="airport-badges-modal relative z-[10000]">
    <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" /><div className="fixed inset-0 overflow-y-auto p-3 sm:p-6"><div className="flex min-h-full items-center justify-center"><Dialog.Panel className={`ab-dialog-panel ${large ? 'ab-dialog-large' : ''} w-full rounded-2xl bg-white shadow-xl`}>
      <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-slate-200 bg-white px-5 py-4"><div>{kicker && <p className="ab-eyebrow">{kicker}</p>}<Dialog.Title className="text-xl font-semibold">{title}</Dialog.Title></div><button type="button" aria-label="Close dialog" disabled={busy} className="p-3" onClick={onClose}><XIcon className="h-5 w-5" /></button></div>
      <form className="ab-dialog-body space-y-5 p-5" onSubmit={event => { event.preventDefault(); if (!busy && !readOnly) onSubmit(); }}>
        {children}{error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-3 border-t border-slate-200 pt-4"><button type="button" className={buttonClass} disabled={busy} onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>{!readOnly && <button className={primaryClass} disabled={busy}>{busy ? 'Working…' : submitLabel}</button>}</div>
      </form></Dialog.Panel></div></div>
  </Dialog>;
}

export async function download(url, filename) {
  const { data } = await api.get(url, { responseType: 'blob' });
  saveAs(data, filename);
}

export function FileLink({ asset, label }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!asset) return null;
  return <div className="min-w-0"><button type="button" disabled={busy} className="min-h-[44px] break-all text-left text-sm font-medium text-red-700 underline disabled:opacity-50" onClick={async () => {
    setBusy(true); setError('');
    try { await download(asset.download_url, asset.filename); } catch (_) { setError('Download failed. Please retry.'); } finally { setBusy(false); }
  }}>{busy ? 'Downloading…' : label || asset.filename}</button>{error && <p className="text-sm text-red-700" role="alert">{error}</p>}</div>;
}

export function UploadField({ label, purpose, value = [], onChange, onBusy, multiple = true, required = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  return <div><label className="block text-sm font-medium text-slate-700">{label}{required && ' *'}<input type="file" multiple={multiple} disabled={busy} className={`${inputClass} file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:p-2`} accept={purpose === 'photo' ? '.jpg,.jpeg,.png,.webp,.heic,.heif' : purpose === 'cancellation' ? '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf' : '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.docx,.xlsx,.pptx'} onChange={async event => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const body = new FormData(); body.append('purpose', purpose); files.forEach(file => body.append('files', file));
    setBusy(true); setError(''); onBusy?.(1);
    try {
      const { data } = await api.post(base + '/assets', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (alive.current) onChange(multiple ? [...value, ...data] : data.slice(0, 1));
    } catch (err) { if (alive.current) setError(errorText(err)); }
    finally { if (alive.current) setBusy(false); onBusy?.(-1); }
  }} /></label>
    {busy && <p role="status" className="mt-2 text-sm">Uploading files…</p>}{error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
    {value.length > 0 && <ul className="mt-2 divide-y divide-slate-100">{value.map(asset => <li key={asset.id} className="flex items-center justify-between gap-3 py-2"><span className="min-w-0 break-all text-sm">{asset.filename}</span><button type="button" className={buttonClass} onClick={() => onChange(value.filter(item => item.id !== asset.id))}>Remove</button></li>)}</ul>}
  </div>;
}

export function useEmployeeAction(employee) {
  const savedKey = useRef(null);
  return async (action, payload) => {
    const signature = JSON.stringify({ action, payload });
    if (savedKey.current?.signature !== signature) savedKey.current = { signature, key: Array.from(crypto.getRandomValues(new Uint8Array(16))).map(value => value.toString(16).padStart(2, '0')).join('') };
    return api.post(`${base}/employees/${employee}/actions/${action}`, { ...payload, request_key: savedKey.current.key });
  };
}

export function PageHeader({ title, description, children }) {
  return <div className="ab-page-head"><div className="min-w-0"><h1 className="break-words text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>{description && <p className="mt-2 text-slate-600">{description}</p>}</div>{children}</div>;
}

export function Pill({ children, tone = '' }) { return <span className={`ab-pill ${tone}`}>{children}</span>; }
export function Panel({ title, actions, children, className = '' }) { return <section className={`ab-panel ${className}`}><div className="ab-panel-head"><h2>{title}</h2>{actions}</div><div className="ab-panel-body">{children}</div></section>; }
export function Facts({ items }) { return <dl className="ab-facts">{items.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>)}</dl>; }
export function displayDate(value) { return value ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value.slice(0, 10)}T12:00:00Z`)) : '—'; }
